import * as React from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Analytics } from '@ixicare/resources';
import { AnalyticsWindow, fetchRange, nextStep, stepStart } from './analyticsWindow';

export type SeriesPoint = { start: number; end: number; value: number };
/** `key` is null for a query without series. */
export type SeriesData = { key: string | null; points: SeriesPoint[] };

const OPEN_PERIOD_REFRESH_MS = 60 * 1000;

type Accumulator = { total: number; weight: number };

/** Every series of a query family (e.g. `alerts.count`) over the window, in the window's step, in
 *  the order the query declares them. Only steps with data are returned. */
export function useAnalyticsSeries(family: string, siteUuid: string | undefined, timeWindow: AnalyticsWindow) {
  const { bucket, step } = timeWindow.resolution;
  const range = fetchRange(timeWindow.start, timeWindow.end, bucket);
  const reachesNow = range.end > Date.now();

  const query = useQuery({
    queryKey: ['analytics', `${family}.${bucket}`, siteUuid, range.start, range.end],
    queryFn: () =>
      Analytics.runAnalyticsQuery(`${family}.${bucket}`, {
        siteUuid: siteUuid!,
        start: new Date(range.start),
        end: new Date(range.end),
      }),
    enabled: !!siteUuid,
    // Finished periods never change; only a range reaching today has anything new to show.
    staleTime: reachesNow ? OPEN_PERIOD_REFRESH_MS : Infinity,
    refetchInterval: reachesNow ? OPEN_PERIOD_REFRESH_MS : false,
    placeholderData: keepPreviousData,
  });

  const series = React.useMemo<SeriesData[]>(() => {
    const data = query.data;
    if (!data) return [];
    const isMean = data.aggregate === 'mean';
    const bySeries = new Map<string | null, Map<number, Accumulator>>(
      (data.series.length ? data.series : [null]).map((key): [string | null, Map<number, Accumulator>] => [
        key,
        new Map(),
      ])
    );

    for (const item of data.buckets) {
      const time = item.start.getTime();
      if (time >= timeWindow.end) continue;
      if (!bySeries.has(item.series)) bySeries.set(item.series, new Map());
      const steps = bySeries.get(item.series)!;
      const key = stepStart(time, step);
      const weight = isMean ? (item.weight ?? 1) : 1;
      const current = steps.get(key) ?? { total: 0, weight: 0 };
      // A mean is carried as a weighted total so 5m buckets combine into exact hourly means.
      steps.set(key, { total: current.total + item.value * weight, weight: current.weight + weight });
    }

    return Array.from(bySeries, ([key, steps]) => ({
      key,
      points: Array.from(steps, ([start, { total, weight }]) => ({
        start,
        end: nextStep(start, step),
        value: isMean ? total / weight : total,
      }))
        .filter((point) => point.end > timeWindow.start)
        .sort((a, b) => a.start - b.start),
    }));
  }, [query.data, step, timeWindow.start, timeWindow.end]);

  return { series, isLoading: query.isLoading, isError: query.isError, isFetching: query.isFetching };
}
